import { INTERVIEW_METHODS, type ScheduleInterviewRequest } from '@aysar/contracts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ModalData } from '@/shared/store/reducers/modal/modal.types';

import { useScheduleInterviewMutation } from '../../services/verification/verification.api';

export interface UseScheduleInterviewModalLogic {
  caseId: string | null;
  fields: FieldDef[];
  /** What the form mounts with — RHF reads `defaultValues` once, at mount. */
  initialValues: Partial<ScheduleInterviewRequest>;
  isRescheduling: boolean;
  submit: (values: ScheduleInterviewRequest) => Promise<void>;
}

/**
 * Confirming the appointment the member asked for.
 *
 * ## The form is the contract's, not a copy of it
 *
 * `DynamicForm` takes `ScheduleInterviewSchema` straight from `@aysar/contracts`
 * (U3) and this hook supplies only presentation: which control renders each
 * field and what it is called in the operator's language.
 *
 * ## `locationOrLink` is `nullable`, not `optional`, and that decides the field
 *
 * The contract types it `.trim().min(1).max(500).nullable()` and the key is
 * **required** — a phone call has no place to be, but the property must still be
 * there. So the field carries `isEmptyNull`: an empty box submits `null`. The
 * two alternatives both fail, and it is worth writing down which way: leaving
 * `''` fails the minimum, and stripping the key fails `.strict()` with
 * "Required" — both on a control the operator emptied on purpose.
 *
 * It is also **always visible**, rather than shown only for a video or in-person
 * method. A field that appears and disappears as a select changes moves
 * everything under it, and the api accepts `null` for every method anyway — so
 * hiding it would buy a layout jump and a second rule to keep in step with the
 * server's.
 *
 * ## The instant is an instant everywhere
 *
 * `scheduledAt` is an offset-bearing ISO instant, and `DateTimeInput` is the one
 * place a wall clock exists (P13c2a is why that is a rule rather than a habit).
 * `min` is "now", read once when the dialog mounts and passed as an instant like
 * everything else — the control converts it.
 */
export function useScheduleInterviewModalLogic(
  modalData: ModalData | undefined,
  onClose: () => void,
): UseScheduleInterviewModalLogic {
  const { t } = useTranslation('verification');
  const toast = useToast();

  /**
   * "Now", read ONCE when the dialog opens.
   *
   * A `new Date()` evaluated during render is a new value every pass, so the
   * picker's `min` would change identity constantly and the field would re-key
   * under the operator while they were typing. Read at mount, which is also the
   * moment the bound actually means something.
   */
  const [nowIso] = useState(() => new Date().toISOString());

  const [scheduleInterview] = useScheduleInterviewMutation();

  const caseId = readString(modalData?.['caseId']);
  const scheduledAt = readString(modalData?.['scheduledAt']);
  const method = readString(modalData?.['method']);
  const locationOrLink = readString(modalData?.['locationOrLink']);

  const fields = useMemo<FieldDef[]>(
    () => [
      {
        name: 'method',
        kind: 'select',
        label: t('interview.schedule.method'),
        isRequired: true,
        // From the code registry, never from the member's request: staff may
        // legitimately confirm a different way of holding it, and a list built
        // from one value could only ever offer that value.
        options: INTERVIEW_METHODS.map((value) => ({
          value,
          label: t(`interview.methodValue.${value}`),
        })),
        colSpan: 'full',
      },
      {
        name: 'scheduledAt',
        kind: 'datetime',
        label: t('interview.schedule.scheduledAt'),
        description: t('interview.schedule.scheduledAtHint'),
        isRequired: true,
        // The picker refuses the past as well as the schema, so the operator
        // meets the rule at the control rather than after pressing Confirm.
        min: nowIso,
        colSpan: 'full',
      },
      {
        name: 'locationOrLink',
        kind: 'text',
        label: t('interview.schedule.locationOrLink'),
        description: t('interview.schedule.locationOrLinkHint'),
        maxLength: 500,
        // Empty means `null` — see the hook's own doc for why not absent.
        isEmptyNull: true,
        colSpan: 'full',
        invalidMessage: t('interview.schedule.locationOrLinkRequired'),
      },
    ],
    [nowIso, t],
  );

  const submit = useCallback(
    async (values: ScheduleInterviewRequest) => {
      if (caseId === null) return;

      // Rejecting is the contract with `DynamicForm`: it maps the refusal onto
      // the fields, and the dialog stays open showing it. Catching here would
      // hide the server's own message.
      await scheduleInterview({ id: caseId, ...values }).unwrap();

      toast.success(t('interview.schedule.success'));
      onClose();
    },
    [caseId, onClose, scheduleInterview, t, toast],
  );

  return {
    caseId,
    fields,
    initialValues: {
      ...(method === null ? {} : { method: readMethod(method) }),
      ...(scheduledAt === null ? {} : { scheduledAt }),
      locationOrLink,
    },
    isRescheduling: scheduledAt !== null,
    submit,
  };
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** Narrows a payload's method. An unknown one seeds nothing rather than a cast. */
function readMethod(value: string): ScheduleInterviewRequest['method'] | undefined {
  return INTERVIEW_METHODS.find((method) => method === value);
}
